import { useState } from "react";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { smoothPageTransition } from "utils/pageTransitionUtils";
import { ADD_SELLER_PRODUCT_MUTATION } from "api/graphql/mutations/AddSellerProductMutation";
import { Attribute, AttributeGroup, ProductFormValues } from "components/products/types";
import { formatProductPayload } from "./productFormUtils";

interface UseProductPublishProps {
  formValues: ProductFormValues;
  characteristicsAttributes: Attribute[];
  selectedAttributes: Attribute[];
  generatedVariants: any[];
  commonAttributeGroups: AttributeGroup[];
  allTabGroups: AttributeGroup[];
  onClear: () => void;
}

const useProductPublish = ({
  formValues,
  characteristicsAttributes,
  selectedAttributes,
  generatedVariants,
  commonAttributeGroups,
  allTabGroups,
  onClear
}: UseProductPublishProps) => {
  const [loading, setLoading] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [addSellerProduct, { data }] = useMutation(ADD_SELLER_PRODUCT_MUTATION);
  const navigate = useNavigate();

  const validateForm = () => {
    const invalidFields: string[] = [];

    // Validate fields in common attribute groups
    commonAttributeGroups.forEach((group) => {
      if (typeof group.attributes !== "string") {
        const excludedAttributes = ["category_ids", "sku"];
        group.attributes.forEach((attribute) => {
          if (
            attribute.is_required === "1" &&
            !formValues[attribute.attribute_code] &&
            !excludedAttributes.includes(attribute.attribute_code)
          ) {
            invalidFields.push(attribute.attribute_code);
          }
        });
      }
    });

    // Validate fields in ProductTabs
    allTabGroups.forEach((group) => {
      if (typeof group.attributes !== "string") {
        group.attributes.forEach((attribute) => {
          if (
            attribute.is_required === "1" &&
            !formValues[attribute.attribute_code]
          ) {
            invalidFields.push(attribute.attribute_code);
          }
        });
      }
    });

    // Special validation: When special_price is set, special_from_date and special_to_date are required
    if (formValues["special_price"] && formValues["special_price"].toString().trim() !== '') {
      if (!formValues["special_from_date"]) {
        invalidFields.push("special_from_date");
      }
      if (!formValues["special_to_date"]) {
        invalidFields.push("special_to_date");
      }
    }

    return invalidFields;
  };

  // Check if any fields in a specific tab group are missing
  const checkForTabErrors = (invalidFields: string[], tabName: string): boolean => {
    // Define fields for each tab
    const tabFieldsMap: Record<string, string[]> = {
      'Offre': [
        'price',
        'special_price',
        'special_from_date', 
        'special_to_date',
        'politique_de_retour'
      ],
      'Informations Logistiques': [
        'mode_expedition',
        'weight',
        'dimensions_de_l_outil_largeur1',
        'dimensions_de_l_outil_hauteur1',
        'longueur25'
      ]
    };
    
    const tabFields = tabFieldsMap[tabName] || [];
    return tabFields.some(field => invalidFields.includes(field));
  };

  // Check tabs in order of priority
  const determineTabToShow = (invalidFields: string[]): string | null => {
    // First check Offre tab
    if (checkForTabErrors(invalidFields, 'Offre')) {
      return 'Offre';
    }
    
    // Then check Informations Logistiques tab
    if (checkForTabErrors(invalidFields, 'Informations Logistiques')) {
      return 'Informations Logistiques';
    }
    
    return null; // No specific tab needs to be shown
  };

  // Validate variant prices
  const validateVariantPrices = () => {
    if (!generatedVariants || generatedVariants.length === 0) {
      return { valid: true, invalidVariants: [] };
    }
    
    const invalidVariants = generatedVariants.filter(variant => {
      return !variant.price || parseFloat(variant.price) <= 0;
    });
    
    return {
      valid: invalidVariants.length === 0,
      invalidVariants
    };
  };

  const handlePublishProduct = async () => {
    // Set loading state to true when starting the publish process
    setLoading(true);
    
    // Validate required fields
    const missingFields = validateForm();

    // Check for category
    if (!formValues.category?.level2) {
      missingFields.push("category");
    }
    
    // Check if gallery is required and missing
    const galleryRequired = commonAttributeGroups.some(group => {
      if (typeof group.attributes !== "string") {
        return group.attributes.some(attr => 
          attr.attribute_code === "gallery" && 
          attr.is_required === "1" && 
          (!formValues.gallery || formValues.gallery.length === 0)
        );
      }
      return false;
    });
    
    if (galleryRequired) {
      missingFields.push("gallery");
    }
    
    // Check variant prices
    const variantPricesValidation = validateVariantPrices();
    if (!variantPricesValidation.valid) {
      toast.error("All variant prices must be greater than 0");
      setLoading(false);
      
      // Highlight the variants table
      setTimeout(() => {
        const variantsTable = document.querySelector('.product-variants-table');
        if (variantsTable) {
          variantsTable.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      return;
    }

    if (missingFields.length > 0) {
      toast.error("Please fill in the required fields");
      setInvalidFields(missingFields);
      setLoading(false);
      
      // Check tabs in order of priority
      const tabToShow = determineTabToShow(missingFields);
      
      if (tabToShow) {
        // Dispatch a custom event that the AddProduct component can listen for
        const tabSwitchEvent = new CustomEvent('switch-to-tab', { 
          detail: { tabName: tabToShow } 
        });
        document.dispatchEvent(tabSwitchEvent);
        
        // Wait a bit before scrolling to make sure the tab has switched
        setTimeout(() => {
          const firstErrorElement = document.querySelector('.is-invalid, .is-invalid-container, .is-invalid-wrapper');
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      } else {
        // Just scroll to the first error field
        setTimeout(() => {
          const firstErrorElement = document.querySelector('.is-invalid, .is-invalid-container, .is-invalid-wrapper');
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      
      return;
    }

    // Prepare the product payload
    const productInput = formatProductPayload(
      formValues,
      characteristicsAttributes,
      selectedAttributes,
      generatedVariants
    );

    // Send GraphQL Mutation
    try {
      const { data } = await addSellerProduct({
        variables: { input: { productData: productInput } },
      });

      if (data?.addSellerProduct?.success) {
        toast.success("Product published successfully.");
        
        // Apply smooth page transition animation before navigating to the products page
        smoothPageTransition(navigate, '/products');
        
        // Still call onClear, but the page will be changing anyway
        onClear();
      } else {
        toast.error(data?.addSellerProduct?.message || "Failed to publish the product.");
      }
    } catch (error) {
      console.error("Error publishing product:", error);
      toast.error("Error publishing product.");
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    invalidFields,
    setInvalidFields,
    handlePublishProduct
  };
};

export default useProductPublish;
